import { useAtom } from "@dbeining/react-atom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage, Field, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Form, Image } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import VerificationInput from "react-verification-input";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { authenticationService } from "../../services";

interface stateType {
    from: { pathname: string };
}

const SignInPage = () => {
    let navigate = useNavigate();
    let location = useLocation();
    let from = (location.state as stateType)?.from?.pathname || "/";
    const currentUser = useAtom(userAtom);
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [twoFactorQrCode, setTwoFactorQrCode] = useState("");
    const [usernameEntered, setUsernameEntered] = useState("");
    const [passwordEntered, setPasswordEntered] = useState("");

    const twoFaRef = useRef<any>(null);

    useEffect(() => {
        // redirect to home if already logged in
        if (currentUser) {
            navigate("/", { state: { message: "Ok" } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (twoFactorRequired && twoFaRef.current) {
            // Focus the first input after a short delay to ensure the component is rendered
            setTimeout(() => {
                if (twoFaRef.current) {
                    twoFaRef.current.focus();
                }
            }, 100);
        }
    }, [twoFactorRequired]);

    return (
        <>
            <Helmet>
                <title>Sign In - Froom Tracker</title>
            </Helmet>

            <div className="login-page p-3">
                <div className="login-brand-section">
                    <Image src="/images/froom-waste-tracker-logo.svg" className="login-brand-logo h-20rem froom-brand" />
                    <Image
                        src="/images/waste-tracking-system-strapline.png"
                        alt="Froom waste tracking system"
                        className="login-strapline-image"
                    />
                </div>
                <div className="login-form-container">
                            {!twoFactorRequired ? (
                                <>
                                    <h1 className="password-title">Sign in</h1>
                                    <Formik
                                        initialValues={{
                                            username: "",
                                            password: "",
                                            showPassword: false,
                                        }}
                                        validationSchema={Yup.object().shape({
                                            username: Yup.string().required("Username is required"),
                                            password: Yup.string().required("Password is required"),
                                        })}
                                        onSubmit={async ({ username, password }, { setStatus, setSubmitting }) => {
                                            setStatus();
                                            await authenticationService.login(username, password).then(
                                                (response) => {
                                                    if (response.status === "Failure") {
                                                        setSubmitting(false);
                                                        setStatus(response.message);
                                                    } else if (response.status === "AuthenticationRequired") {
                                                        setTwoFactorRequired(true);
                                                        setUsernameEntered(username);
                                                        setPasswordEntered(password);
                                                        if (twoFaRef && twoFaRef.current) twoFaRef.current.focus();
                                                    } else if (response.advisories !== null && response.advisories.twoFaSetupRequired !== null && response.advisories.twoFaSetupRequired === true) {
                                                        navigate("/twofactorSetup", {
                                                            replace: true,
                                                            state: { from: location }
                                                        });
                                                    } else {
                                                        navigate(from, {
                                                            replace: true,
                                                        });
                                                    }
                                                },
                                                (error) => {
                                                    setSubmitting(false);
                                                    setStatus(error);
                                                }
                                            );
                                        }}
                                    >
                                        {({ values, errors, status, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                                            <Form noValidate onSubmit={handleSubmit}>
                                                <div className="login-form-group">
                                                    <label>Email address</label>
                                                    <Field name="username" type="text" className={"login-form-group input" + (errors.username && touched.username ? " is-invalid" : "")} />
                                                    <ErrorMessage name="username" component="div" className="invalid-feedback" />
                                                </div>
                                                <div className="login-form-group">
                                                    <label>Password</label>
                                                    <div className="password-input-wrapper">
                                                        <Field
                                                            name="password"
                                                            type={values.showPassword ? "text" : "password"}
                                                            className={"password-input-wrapper input" + (errors.password && touched.password ? " is-invalid" : "")}
                                                        />
                                                        <button 
                                                            type="button"
                                                            className="password-toggle-visibility" 
                                                            onClick={() => setFieldValue("showPassword", !values.showPassword)}
                                                        >
                                                            <FontAwesomeIcon icon={values.showPassword ? faEyeSlash : faEye} />
                                                        </button>
                                                    </div>
                                                    <ErrorMessage name="password" component="div" className="invalid-feedback" />
                                                </div>
                                                <div className="login-button-row">
                                                    <button
                                                        type="button"
                                                        className="btn-secondary-custom"
                                                        disabled={isSubmitting}
                                                        onClick={() =>
                                                            navigate("/forgotpassword", {
                                                                state: {
                                                                    message: "Ok",
                                                                },
                                                            })
                                                        }
                                                    >
                                                        Forgot password?
                                                    </button>
                                                    <button className="btn-primary-custom" disabled={isSubmitting || !values.username || !values.password} type="submit">
                                                        {isSubmitting ? <LoadingSpinner text="Signing in..." /> : "Sign In"}
                                                    </button>
                                                </div>
                                                {status && <div className={"alert alert-danger"}>{status}</div>}
                                            </Form>
                                        )}
                                    </Formik>
                                </>
                            ) : (
                                <>
                                    <h1 className="password-title">Two-Factor Authentication</h1>
                                    <p>Enter the authentication code from the authentication app on your mobile device</p>
                                    <Formik
                                        initialValues={{}}
                                        onSubmit={async (_, { setStatus, setSubmitting }) => {
                                            setStatus();
                                            await authenticationService.login(usernameEntered, passwordEntered, twoFactorQrCode).then(
                                                (response) => {
                                                    if (response.status === "Failure") {
                                                        setSubmitting(false);
                                                        setStatus(response.message);
                                                    } else if (response.status === "AuthenticationRequired") {
                                                        console.log("Error", response);
                                                    } else {
                                                        navigate(from, {
                                                            replace: true,
                                                        });
                                                    }
                                                },
                                                (error) => {
                                                    setSubmitting(false);
                                                    setStatus(error);
                                                }
                                            );
                                        }}
                                    >
                                        {({ status, isSubmitting, handleSubmit }) => (
                                            <Form noValidate onSubmit={handleSubmit}>
                                                <div className="login-form-group">
                                                    <label htmlFor="disabledUsername">Email address</label>
                                                    <input id="disabledUsername" placeholder={usernameEntered} disabled readOnly className="login-form-group input" />
                                                </div>
                                                <div className="text-center d-flex justify-content-center">
                                                    <div className="tfa-otp-container">
                                                        <div className="label">Validation Code</div>
                                                        <div className="tfa-otp-inputs">
                                                            <VerificationInput
                                                                ref={twoFaRef}
                                                                value={twoFactorQrCode}
                                                                length={6}
                                                                validChars={"0-9"}
                                                                placeholder={""}
                                                                onChange={(value) => setTwoFactorQrCode(value)}
                                                                classNames={{
                                                                    container: "tfa-otp-input"
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-between signin-2fa-button-container sign-in-action-buttons">
                                                    <button 
                                                        type="button" 
                                                        className="btn-secondary-custom" 
                                                        style={{ marginLeft: '-6px' }}
                                                        onClick={() => {
                                                            setTwoFactorRequired(false);
                                                            setTwoFactorQrCode("");
                                                            setUsernameEntered("");
                                                            setPasswordEntered("");
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="btn-primary-custom" 
                                                        disabled={isSubmitting || twoFactorQrCode.length !== 6} 
                                                        type="submit"
                                                        style={{ marginRight: '-6px' }}
                                                    >
                                                        {isSubmitting ? <LoadingSpinner text="Validating..." /> : "Validate"}
                                                    </button>
                                                </div>
                                                {status && <div className={"alert alert-danger"}>{status}</div>}
                                            </Form>
                                        )}
                                    </Formik>
                                </>
                            )}
                </div>
            </div>
        </>
    );
};

export { SignInPage };
